/**
 * feat-298 — the floating table of contents for `/quiz-review` (spec §3,
 * design NOTES §2, §8).
 *
 * One tree, three shells:
 *   - `ReviewTocPanel`   docked, sticky, beside the content (≥ lg; ≥ xl in Both)
 *   - `ReviewTocSheet`   modal bottom sheet (< lg)
 *   - `ReviewTocPopover` non-modal popover from the Contents pill (Both, lg–xl)
 *
 * Rows are real buttons: a disclosure button and a separate entry button, so
 * expanding a TOC node never jumps the page. No `role="tree"` — a list of
 * buttons is accessible without the full arrow-key contract (NOTES §11).
 * The TOC's expand state is its own, separate from the body's collapse state.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { CornerLeftUp, ListTree, PanelLeftClose, X } from 'lucide-react';
import { Chevron, FOCUS_RING } from './Collapsible';
import { QUIZ_REVIEW_NAV_COPY as COPY } from './navCopy';
import { TocNode } from './tocModel';

export { QUIZ_REVIEW_NAV_COPY } from './navCopy';

// ============================================================
// TOC expand state
// ============================================================

export interface TocExpansion {
  isExpanded: (n: TocNode) => boolean;
  toggle: (n: TocNode) => void;
}

const defaultOpen = (n: TocNode) => n.level === 'track' || n.level === 'unassigned';

/**
 * Tracks open, Lessons closed by default (design Q5), with the scroll-spy's
 * active path ADDED on top — never collapsing what the reader opened. Reset when
 * `resetKey` changes (tenant switch).
 */
export function useTocExpansion(activePath: string[], resetKey: unknown): TocExpansion {
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  const [closed, setClosed] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    setOpened(new Set());
    setClosed(new Set());
  }, [resetKey]);

  // The active entry's ANCESTORS (its Track and Lesson) — not the entry itself.
  const ancestors = activePath.slice(0, -1);
  const signature = ancestors.join('|');
  useEffect(() => {
    if (!signature) return;
    const ids = signature.split('|');
    setOpened((prev) => (ids.every((id) => prev.has(id)) ? prev : new Set([...prev, ...ids])));
    setClosed((prev) => {
      if (!ids.some((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, [signature]);

  const isExpanded = useCallback(
    (n: TocNode) => opened.has(n.id) || (!closed.has(n.id) && defaultOpen(n)),
    [opened, closed],
  );
  const toggle = useCallback(
    (n: TocNode) => {
      const open = isExpanded(n);
      setOpened((prev) => {
        const next = new Set(prev);
        if (open) next.delete(n.id);
        else next.add(n.id);
        return next;
      });
      setClosed((prev) => {
        const next = new Set(prev);
        if (open) next.add(n.id);
        else next.delete(n.id);
        return next;
      });
    },
    [isExpanded],
  );
  return { isExpanded, toggle };
}

// ============================================================
// The tree
// ============================================================

export interface TocTreeProps {
  nodes: TocNode[];
  /** Node ids from root to the active entry (empty ⇒ nothing active). */
  activePath: string[];
  expansion: TocExpansion;
  onJump: (target: string) => void;
}

const ACTIVE_ENTRY =
  'relative bg-coral-light text-ink font-semibold before:absolute before:left-0 ' +
  'before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-coral';

function entryLabel(n: TocNode): string {
  if (n.level === 'lesson') return `${n.number}. ${n.emoji ? `${n.emoji} ` : ''}${n.title}`;
  if (n.level === 'drill') return `${n.number}. ${n.title}`;
  return n.title;
}

const TocRow: React.FC<
  TocTreeProps & { node: TocNode; touch: boolean; depth: number }
> = ({ node, touch, depth, nodes, activePath, expansion, onJump }) => {
  const isActive = activePath.length > 0 && activePath[activePath.length - 1] === node.id;
  const onPath = !isActive && activePath.includes(node.id);
  const stub = !!node.stubOwnerTitle;
  const hasDisclosure = !stub && node.level !== 'drill' && node.children.length > 0;
  const open = hasDisclosure && expansion.isExpanded(node);
  const label = entryLabel(node);

  let tone: string;
  if (node.level === 'track') tone = 'font-semibold text-ink';
  else if (node.level === 'unassigned') tone = 'font-semibold italic text-inkMuted';
  else if (node.level === 'lesson') tone = stub ? 'text-inkMuted' : 'text-ink';
  else tone = 'text-inkMuted ml-1';
  if (onPath) tone += ' font-semibold text-ink';

  const disclosureSize = touch ? 'w-8 h-10' : 'w-6 h-7';
  const rowPad = touch ? 'py-2' : 'py-1';
  const stubTitle = stub ? `${COPY.shownUnder} ${node.stubOwnerTitle}` : undefined;

  return (
    <li
      className={
        node.level === 'unassigned'
          ? 'pt-2 mt-2 border-t border-dashed border-softBorder'
          : undefined
      }
    >
      <div className="flex items-start">
        {hasDisclosure ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? COPY.collapse(node.title) : COPY.expand(node.title)}
            onClick={() => expansion.toggle(node)}
            className={`shrink-0 grid place-items-center rounded-md ${disclosureSize} text-inkMuted hover:bg-cream-dark ${FOCUS_RING}`}
          >
            <Chevron open={open} className="w-3.5 h-3.5" />
          </button>
        ) : (
          node.level !== 'drill' && <span aria-hidden className={`${disclosureSize} shrink-0`} />
        )}
        <button
          type="button"
          data-toc-target={node.target}
          aria-current={isActive ? 'location' : undefined}
          aria-label={stub ? `${label} — ${stubTitle}` : undefined}
          title={stubTitle}
          onClick={() => onJump(node.target)}
          className={`flex-1 min-w-0 text-left rounded-md px-2 ${rowPad} leading-snug text-[13px] ${FOCUS_RING} ${
            // The active entry keeps its coral wash under the pointer — right after a
            // jump the pointer is still on it (design review S1).
            isActive ? `${ACTIVE_ENTRY} hover:bg-coral-light` : `hover:bg-cream-dark ${tone}`
          }`}
        >
          <span className="line-clamp-2" title={node.title}>
            {node.level === 'lesson' && (
              <span className="text-inkMuted font-normal mr-1">{node.number}.</span>
            )}
            {node.level === 'drill' && <span className="mr-1">{node.number}.</span>}
            {node.level === 'lesson' && node.emoji && <>{node.emoji} </>}
            <span className={node.active ? undefined : 'italic text-inkMuted'}>{node.title}</span>
            {!node.active && (
              <span className="ml-1.5 text-[10px] font-semibold not-italic uppercase tracking-wide text-inkMuted">
                {COPY.inactiveMarker}
              </span>
            )}
            {stub && <CornerLeftUp className="inline w-3 h-3 ml-1 align-[-1px]" aria-hidden />}
          </span>
        </button>
      </div>
      {open && (
        <ul className="ml-[11px] pl-1.5 border-l border-softBorder space-y-0.5 mt-0.5">
          {node.children.map((c) => (
            <TocRow
              key={c.id}
              node={c}
              touch={touch}
              depth={depth + 1}
              nodes={nodes}
              activePath={activePath}
              expansion={expansion}
              onJump={onJump}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export const TocTree: React.FC<TocTreeProps & { touch?: boolean }> = ({ touch = false, ...props }) => (
  <ul className="space-y-0.5">
    {props.nodes.map((n) => (
      <TocRow key={n.id} node={n} touch={touch} depth={0} {...props} />
    ))}
  </ul>
);

/**
 * Keep the active entry visible by scrolling the PANEL only. Never
 * `entry.scrollIntoView()` — that also scrolls the window and fights the reader
 * (NOTES §3).
 */
function useKeepActiveVisible(scrollerRef: React.RefObject<HTMLElement | null>, activeId: string | undefined) {
  // Which active id has already been brought into view. The entry for a new id is
  // often NOT in the DOM on the render that changes it — its Lesson auto-expands in
  // the TOC a render later — so this runs after every render and acts once the
  // entry appears, instead of only on the render where `activeId` changed.
  const settledRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!activeId) {
      // Nothing current (above the first Track): forget, so returning to the same
      // Drill brings it back into view.
      settledRef.current = undefined;
      return;
    }
    if (!scroller || settledRef.current === activeId) return;
    const entry = scroller.querySelector<HTMLElement>('[aria-current="location"]');
    if (!entry) return;
    settledRef.current = activeId;
    const margin = 24;
    const top =
      entry.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    const bottom = top + entry.offsetHeight;
    if (top < scroller.scrollTop + margin || bottom > scroller.scrollTop + scroller.clientHeight - margin) {
      scroller.scrollTop = Math.max(0, top - scroller.clientHeight / 2 + entry.offsetHeight / 2);
    }
  });
}

const TocHeader: React.FC<{
  headingId?: string;
  button: React.ReactNode;
}> = ({ headingId, button }) => (
  <div className="flex items-center justify-between pl-4 pr-2 pt-3 pb-2 border-b border-softBorder">
    <h2 id={headingId} className="text-[11px] font-semibold uppercase tracking-wide text-inkMuted">
      {COPY.tocHeading}
    </h2>
    {button}
  </div>
);

// ============================================================
// Docked panel (≥ lg / ≥ xl in Both)
// ============================================================

export const ReviewTocPanel: React.FC<
  TocTreeProps & {
    /** Breakpoint visibility classes, e.g. `hidden lg:flex`. */
    visibility: string;
    onHide: () => void;
    /** feat-302 — the View as line, directly under the Contents header row. */
    banner?: React.ReactNode;
  }
> = ({ visibility, onHide, banner, ...tree }) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  useKeepActiveVisible(scrollerRef, tree.activePath[tree.activePath.length - 1]);
  return (
    <nav
      aria-label={COPY.tocLabel}
      className={`${visibility} sticky self-start rounded-2xl bg-white ring-1 ring-black/5 shadow-sm flex-col`}
      style={{
        top: 'calc(var(--review-chrome-top) + 16px)',
        maxHeight: 'calc(100dvh - var(--review-chrome-top) - 32px)',
      }}
    >
      <TocHeader
        button={
          <button
            type="button"
            aria-label={COPY.hideContents}
            title={COPY.hideContents}
            onClick={onHide}
            className={`shrink-0 grid place-items-center rounded-md w-8 h-8 text-inkMuted hover:text-ink hover:bg-cream-dark ${FOCUS_RING}`}
          >
            <PanelLeftClose className="w-4 h-4" aria-hidden />
          </button>
        }
      />
      {banner}
      <div ref={scrollerRef} className="overflow-y-auto overscroll-contain px-2 py-2">
        <TocTree {...tree} />
      </div>
    </nav>
  );
};

// ============================================================
// Contents pill
// ============================================================

export const ContentsPill = React.forwardRef<
  HTMLButtonElement,
  { open: boolean; visibility: string; onClick: () => void; label?: string }
>(({ open, visibility, onClick, label }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-haspopup="dialog"
    aria-expanded={open}
    onClick={onClick}
    className={`${visibility} fixed z-40 bottom-4 left-4 md:bottom-5 md:left-5 items-center gap-2 h-10 pl-3 pr-4 rounded-full bg-white/95 backdrop-blur-sm shadow-lg ring-1 ring-black/5 text-sm font-semibold text-ink hover:bg-cream-dark ${FOCUS_RING}`}
  >
    <ListTree className="w-4 h-4" aria-hidden />
    {label ?? COPY.contentsButton}
  </button>
));
ContentsPill.displayName = 'ContentsPill';

// ============================================================
// Bottom sheet (< lg) and popover (Both, lg–xl)
// ============================================================

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface OverlayProps extends TocTreeProps {
  onClose: () => void;
  /** feat-302 — the View as line at the top of the sheet / popover. */
  banner?: React.ReactNode;
}

/**
 * Modal bottom sheet. Locks body scroll, focuses Close, traps Tab, and closes
 * on Esc / backdrop / Close WITHOUT jumping. Choosing an entry is handled by the
 * caller's `onJump`, which closes the sheet first and then jumps (NOTES §4.5).
 */
export const ReviewTocSheet: React.FC<OverlayProps> = ({ onClose, banner, ...tree }) => {
  const headingId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  useKeepActiveVisible(scrollerRef, tree.activePath[tree.activePath.length - 1]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <div
        aria-hidden
        data-testid="toc-sheet-backdrop"
        className="fixed inset-0 z-[60] bg-black/40"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onKeyDown={onKeyDown}
        className="fixed inset-x-0 bottom-0 z-[60] max-h-[85dvh] flex flex-col rounded-t-3xl bg-white shadow-2xl"
      >
        <div aria-hidden className="mx-auto mt-2 h-1 w-10 rounded-full bg-black/15" />
        <TocHeader
          headingId={headingId}
          button={
            <button
              ref={closeRef}
              type="button"
              aria-label={COPY.closeContents}
              title={COPY.closeContents}
              onClick={onClose}
              className={`shrink-0 grid place-items-center rounded-md w-10 h-10 text-inkMuted hover:text-ink hover:bg-cream-dark ${FOCUS_RING}`}
            >
              <X className="w-5 h-5" aria-hidden />
            </button>
          }
        />
        {banner}
        <div
          ref={scrollerRef}
          className="flex-1 overflow-y-auto overscroll-contain px-2 pt-2 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
        >
          <nav aria-label={COPY.tocLabel}>
            <TocTree {...tree} touch />
          </nav>
        </div>
      </div>
    </>
  );
};

/**
 * Non-modal popover above the Contents pill, for Both mode between lg and xl
 * where docking would leave ~300 px per language. Closes on Esc, outside
 * mousedown, ✕ and on choosing an entry. No backdrop, no scroll lock.
 */
export const ReviewTocPopover: React.FC<OverlayProps & { pillRef: React.RefObject<HTMLElement | null> }> = ({
  onClose,
  pillRef,
  banner,
  ...tree
}) => {
  const headingId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  useKeepActiveVisible(scrollerRef, tree.activePath[tree.activePath.length - 1]);

  useEffect(() => {
    closeRef.current?.focus();
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || pillRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [onClose, pillRef]);

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-labelledby={headingId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
      className="fixed z-40 left-5 bottom-[76px] max-w-[320px] w-full rounded-2xl bg-white shadow-xl ring-1 ring-black/5 flex flex-col"
      style={{ maxHeight: 'calc(100dvh - var(--review-chrome-top) - 100px)' }}
    >
      <TocHeader
        headingId={headingId}
        button={
          <button
            ref={closeRef}
            type="button"
            aria-label={COPY.closeContents}
            title={COPY.closeContents}
            onClick={onClose}
            className={`shrink-0 grid place-items-center rounded-md w-8 h-8 text-inkMuted hover:text-ink hover:bg-cream-dark ${FOCUS_RING}`}
          >
            <X className="w-4 h-4" aria-hidden />
          </button>
        }
      />
      {banner}
      <div ref={scrollerRef} className="overflow-y-auto overscroll-contain px-2 py-2">
        <nav aria-label={COPY.tocLabel}>
          <TocTree {...tree} />
        </nav>
      </div>
    </div>
  );
};
